import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, faqSchema } from '../utils/structuredData';
import { getAppSupportBySlug } from '../utils/appsData';
import { ArrowRightIcon, LifeBuoyIcon, MailIcon, BookOpenIcon, ExternalLinkIcon, ChevronDownIcon, MessageSquareIcon } from 'lucide-react';
export function AppSupportPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const app = getAppSupportBySlug(slug);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  usePageSEO({
    title: app ? `${app.name} Support | Design Flow Digitals` : 'App Support | Design Flow Digitals',
    description: app ? `Get help, setup guides, and troubleshooting for ${app.name}.` : 'Support center for Design Flow Digitals Shopify apps.',
    canonical: `/support/${slug}`,
    keywords: app ? `${app.name} support, ${app.name} help, Shopify app support, Design Flow Digitals support` : 'Shopify app support',
    jsonLd: app ? [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Support',
      path: '/support'
    }, {
      name: `${app.name} Support`,
      path: `/support/${slug}`
    }]), ...(app.troubleshooting && app.troubleshooting.length > 0 ? [faqSchema(app.troubleshooting.map(f => ({
      question: f.q,
      answer: f.a
    })))] : [])] : undefined
  });
  if (!app) {
    return <div className="pt-40 pb-32 px-6 text-center" data-id="element-800">
        <h1 className="text-4xl font-bold mb-4" data-id="element-801">Support page not found</h1>
        <p className="text-gray-400 mb-8" data-id="element-802">
          The app support page you’re looking for doesn’t exist.
        </p>
        <Link to="/support" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-803">
          View Support Center <ArrowRightIcon className="w-5 h-5" data-id="element-804" />
        </Link>
      </div>;
  }
  const Icon = app.icon;
  return <div className="w-full" data-id="element-805">
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden border-b border-white/5" data-id="element-806">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" data-id="element-807" />
        <div className={`absolute top-0 right-1/4 w-96 h-96 ${app.accentBg} rounded-full blur-[120px] pointer-events-none`} data-id="element-808" />

        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10" data-id="element-809">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6" data-id="element-810">
            <div className={`w-16 h-16 rounded-2xl glass-card flex items-center justify-center shrink-0 border ${app.accentBorder} ${app.accentBg}`} data-id="element-811">
              <Icon className={`w-8 h-8 ${app.accentColor}`} data-id="element-812" />
            </div>
            <div data-id="element-813">
              <h1 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-814">
                {app.name} Support
              </h1>
              <p className={`text-lg ${app.accentColor} font-medium`} data-id="element-815">
                {app.tagline}
              </p>
            </div>
          </div>
          <p className="text-xl text-gray-400 leading-relaxed max-w-2xl" data-id="element-816">
            {app.description}
          </p>
        </div>
      </section>

      {/* Quick Contact Band */}
      <section className="py-8 border-b border-white/5 bg-white/[0.02]" data-id="element-817">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left" data-id="element-818">
          <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-819">
            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-820">
              <MailIcon className="w-5 h-5 text-gray-300" data-id="element-821" />
            </div>
            <div data-id="element-822">
              <p className="text-white font-medium" data-id="element-823">Need direct help?</p>
              <p className="text-sm text-gray-400" data-id="element-824">
                Email{' '}
                <a href={`mailto:${app.contact.email}`} className="text-white hover:underline" data-id="element-825">
                  {app.contact.email}
                </a>
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-826">
                Replies usually {app.contact.responseTime}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto" data-id="element-827">
            {app.shopifyUrl && <a href={app.shopifyUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-full glass-card text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-828">
                Open App Store <ExternalLinkIcon className="w-4 h-4" data-id="element-829" />
              </a>}
            <Link to={app.productRoute} className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-full glass-card text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-830">
              View Product Page <ArrowRightIcon className="w-4 h-4" data-id="element-831" />
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20" data-id="element-832">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-833">
          {/* Left Column: Getting Started */}
          <div className="lg:col-span-2 space-y-16" data-id="element-834">
            <div data-id="element-835">
              <div className="flex items-center gap-3 mb-8" data-id="element-836">
                <BookOpenIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-837" />
                <h2 className="text-3xl font-bold" data-id="element-838">Getting Started</h2>
              </div>
              <div className="space-y-8 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-white/10 before:via-white/10 before:to-transparent" data-id="element-839">
                {app.gettingStarted.map((step, i) => <div key={i} className="relative flex items-start gap-6" data-id="element-840">
                    <div className={`w-12 h-12 rounded-full glass-card flex items-center justify-center shrink-0 border ${app.accentBorder} ${app.accentBg} relative z-10`} data-id="element-841">
                      <span className={`font-bold ${app.accentColor}`} data-id="element-842">
                        {i + 1}
                      </span>
                    </div>
                    <div className="pt-2" data-id="element-843">
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-844">
                        {step.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed" data-id="element-845">
                        {step.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Troubleshooting / FAQ */}
            <div data-id="element-846">
              <div className="flex items-center gap-3 mb-8" data-id="element-847">
                <LifeBuoyIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-848" />
                <h2 className="text-3xl font-bold" data-id="element-849">Troubleshooting & FAQ</h2>
              </div>
              <div className="space-y-4" data-id="element-850">
                {app.troubleshooting.map((faq, i) => <div key={i} className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 ${openFaq === i ? `border ${app.accentBorder}` : 'border-white/5 hover:border-white/10'}`} data-id="element-851">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left" data-id="element-852">
                      <span className="font-semibold text-lg pr-8" data-id="element-853">
                        {faq.q}
                      </span>
                      <ChevronDownIcon className={`w-5 h-5 shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180 text-white' : 'text-gray-500'}`} data-id="element-854" />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-855">
                      <p className="px-6 pb-6 text-gray-400 leading-relaxed" data-id="element-856">
                        {faq.a}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column: Sticky Sidebar */}
          <div className="lg:col-span-1" data-id="element-857">
            <div className="sticky top-32 glass-card p-8 rounded-3xl border-white/10" data-id="element-858">
              <div className={`w-12 h-12 rounded-xl mb-6 flex items-center justify-center ${app.accentBg} border ${app.accentBorder}`} data-id="element-859">
                <MessageSquareIcon className={`w-6 h-6 ${app.accentColor}`} data-id="element-860" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-861">Still need help?</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed" data-id="element-862">
                Can't find the answer you're looking for? Our support team is
                ready to help you get the most out of {app.name}.
              </p>
              <Link to="/contact" className="w-full flex items-center justify-center gap-2 bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors" data-id="element-863">
                Contact Support
              </Link>
              <div className="mt-6 pt-6 border-t border-white/10" data-id="element-864">
                <p className="text-xs text-gray-500 text-center" data-id="element-865">
                  Support hours: Mon-Fri, 9am-5pm EST
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}